// v77.71 — Public Help & Support page.
//
// Mirrors the in-app `HelpPage` content (imported from
// `@/lib/helpContent`) but renders it without auth gating, without
// deep-link "Show me where" buttons (those would 404 for unauth users)
// and styled to match the public Landing / About / Privacy / Terms
// surface (calm, soft alternating sections, mobile-first).

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import { Search, Plus, Minus, ArrowLeft, Mail, ArrowRight } from "lucide-react";
import { HELP } from "@/lib/helpContent";

const SUPPORT_EMAIL = "support@coparentconsole.com";
const supportMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Support Request – CoParent Console")}`;

// --- Reusable bits (kept local; not worth a shared module) -----------------

const SectionCard = ({ section, openMap, toggle }) => {
  const Icon = section.icon;
  return (
    <div
      className="border border-[#E5EBEC] dark:border-[#2A3539] bg-white dark:bg-[#161E21] rounded-xl shadow-[0_1px_2px_rgba(15,21,23,0.04)] overflow-hidden"
      data-testid={`public-help-section-${section.id}`}
    >
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E5EBEC] dark:border-[#2A3539] bg-[#F4FAFA] dark:bg-[#0F2F30]/30">
        <span className="w-8 h-8 rounded-md bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 text-[#116B6E] dark:text-[#3FB8BC] inline-flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.6} />
        </span>
        <h2 className="font-heading text-base sm:text-lg font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
          {section.title}
        </h2>
      </div>
      <div className="divide-y divide-[#E5EBEC] dark:divide-[#2A3539]">
        {section.items.map((it) => {
          const key = `${section.id}::${it.q}`;
          const open = !!openMap[key];
          const slug = it.q.replace(/[^\w]+/g, "-").toLowerCase();
          return (
            <div key={it.q} data-testid={`public-help-item-${section.id}-${slug}`}>
              <button
                type="button"
                onClick={() => toggle(key)}
                className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-[#F4FAFA] dark:hover:bg-[#0F2F30]/30 transition-colors min-h-[44px]"
                aria-expanded={open}
              >
                <span className="text-sm font-medium text-[#1A2225] dark:text-[#F4F8F9]">{it.q}</span>
                {open
                  ? <Minus className="w-4 h-4 text-[#6F828A] flex-shrink-0" strokeWidth={1.6} />
                  : <Plus className="w-4 h-4 text-[#6F828A] flex-shrink-0" strokeWidth={1.6} />}
              </button>
              {open && (
                <div
                  className="px-5 pb-4 text-sm text-[#48595E] dark:text-[#B6C2C7] leading-relaxed"
                  data-testid={`public-help-answer-${section.id}-${slug}`}
                >
                  <p>{it.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Page ------------------------------------------------------------------

export default function PublicHelp() {
  const [query, setQuery] = useState("");
  const [openMap, setOpenMap] = useState({});

  const toggle = (key) => setOpenMap((m) => ({ ...m, [key]: !m[key] }));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP;
    const tokens = q.split(/\s+/).filter(Boolean);
    const matchesToken = (it, t) => (
      it.q.toLowerCase().includes(t) ||
      it.a.toLowerCase().includes(t) ||
      (it.keywords || []).some((k) => k.toLowerCase().includes(t))
    );
    return HELP
      .map((s) => ({ ...s, items: s.items.filter((it) => tokens.every((t) => matchesToken(it, t))) }))
      .filter((s) => s.items.length > 0);
  }, [query]);

  // Auto-expand all matching items when there's an active query.
  const effectiveOpenMap = useMemo(() => {
    if (!query.trim()) return openMap;
    const m = {};
    filtered.forEach((s) => s.items.forEach((it) => { m[`${s.id}::${it.q}`] = true; }));
    return m;
  }, [openMap, query, filtered]);

  const totalHits = filtered.reduce((n, s) => n + s.items.length, 0);

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="public-help-page"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-help-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] inline-flex items-center gap-1.5"
            data-testid="public-help-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">Need help?</div>
          <h1 className="font-heading text-[2rem] leading-[1.15] sm:text-5xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            Help &amp; Support
          </h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
            Clear answers to help you use CoParent Console with confidence.
            Search the topics below, or get in touch — we read every email.
          </p>

          {/* Search */}
          <div className="relative mt-7">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F828A]" strokeWidth={1.6} />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Help — try ‘invite’, ‘split’, ‘cancel’, ‘private’"
              className="pl-9 min-h-[44px] bg-white dark:bg-[#161E21]"
              data-testid="public-help-search-input"
            />
            {query && (
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6F828A] tabular-nums"
                data-testid="public-help-search-hits"
              >
                {totalHits} match{totalHits === 1 ? "" : "es"}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 space-y-4">
          {filtered.length === 0 ? (
            <div
              className="border border-dashed border-[#E5EBEC] dark:border-[#2A3539] bg-white dark:bg-[#161E21] rounded-xl py-12 px-6 text-center"
              data-testid="public-help-no-results"
            >
              <Search className="w-8 h-8 mx-auto text-[#6F828A]" strokeWidth={1.5} />
              <p className="mt-3 text-sm font-medium text-[#1A2225] dark:text-[#F4F8F9]">No matches for &quot;{query}&quot;.</p>
              <p className="mt-1 text-sm text-[#48595E] dark:text-[#A8B6BB]">Try a different word — or get in touch below.</p>
            </div>
          ) : (
            filtered.map((s) => (
              <div key={s.id} id={s.id}>
                <SectionCard section={s} openMap={effectiveOpenMap} toggle={toggle} />
              </div>
            ))
          )}
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-white dark:bg-[#0E1416]" data-testid="public-help-contact">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="border border-[#D6F0EF] dark:border-[#1F9FA4]/30 bg-gradient-to-br from-[#F4FAFA] to-white dark:from-[#0F2F30]/40 dark:to-[#0E1416] rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.7} />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading text-xl font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
                  Still need a hand?
                </h3>
                <p className="mt-2 text-sm text-[#48595E] dark:text-[#B6C2C7] leading-relaxed">
                  Email{" "}
                  <a className="text-[#116B6E] dark:text-[#3FB8BC] underline-offset-2 hover:underline" href={`mailto:${SUPPORT_EMAIL}`} data-testid="public-help-support-email">
                    {SUPPORT_EMAIL}
                  </a>{" "}
                  and we&apos;ll get back to you. Helpful things to include:
                  your account email, the device or browser you&apos;re on, and a
                  screenshot if you have one. We&apos;ll never ask for your
                  password.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-5">
                  <Link to="/contact" data-testid="public-help-contact-cta" className="w-full sm:w-auto">
                    <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-11 px-5">
                      Contact us
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <a href={supportMailto} className="w-full sm:w-auto" data-testid="public-help-mailto-cta">
                    <Button variant="outline" className="w-full sm:w-auto h-11 px-5 rounded-md border-[#D6F0EF] dark:border-[#2A3539]">
                      <Mail className="w-4 h-4 mr-2" /> Email support
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">CoParent Console</span>
          </div>
          <p className="text-xs text-[#6F828A] dark:text-[#7A8A91] text-center">
            Proudly Irish-made and Irish-owned.
          </p>
          <div className="flex items-center gap-3 text-xs text-[#6F828A] dark:text-[#7A8A91]">
            <Link to="/about" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">About</Link>
            <span aria-hidden="true">·</span>
            <Link to="/contact" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Contact</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/security" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Security</Link>
          </div>
        </div>
        <LegalFooterInfo />
      </footer>
    </div>
  );
}
